import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/atoms/tabs";
import { useState, useEffect, useMemo } from "react";
import { DriverResumen } from "./partials/DriverResumen";
import { Link, useParams } from "react-router-dom";
import { useDriversApi } from "@/hooks/useDriversApi";
import { AlertCircle, ChevronLeft, Home } from "lucide-react";
import mqtt, { IClientOptions, MqttClient } from "mqtt";
import { DriverInfoPersonalRefactored } from "./partials/DriverInfoPersonalRefactored";
import { getDriverStatusConfig, DRIVER_STATUS } from "@/lib/statusConfig";

const MS_PER_MINUTE = 60000;

export default function DriverDetail() {
  const { id } = useParams<{ id: string }>();
  const { fetchDriverById } = useDriversApi();

  const [data, setData] = useState<any>(null);
  const [realTimeData, setRealTimeData] = useState<any>(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingMqtt, setLoadingMqtt] = useState(true);

  const fetchDriver = async () => {
    if (!id) return;
    try {
      setLoadingPage(true);
      const driverResponse = await fetchDriverById(id);
      setData(driverResponse);
    } catch (error) {
      console.error("Error fetching driver:", error);
    } finally {
      setLoadingPage(false);
    }
  };

  const statusInfo = useMemo(() => {
    // 1. Estado de Sistema (Inactivo)
    if (!data?.driver?.active) {
      return {
        primary: "Inactivo",
        secondary: "Conductor inactivo",
        dotClass: "bg-red-500",
        isAnimate: false,
        time: "",
      };
    }

    // 2. Carga inicial
    if (loadingMqtt && !realTimeData) {
      return {
        primary: "Buscando conductor...",
        secondary: "",
        dotClass: "bg-yellow-500",
        isAnimate: true,
        time: "",
        showSecondary: false,
      };
    }

    // 3. Resolución de Estado Jerárquico
    // Un driver está online si la DB dice que está trabajando O si tenemos telemetría activa que no sea 'offline'
    const isOnline =
      data.driver.driver_working ||
      (!!realTimeData && realTimeData.driver_status !== DRIVER_STATUS.OFFLINE);

    const currentStatusKey =
      realTimeData?.driver_status ||
      (isOnline ? DRIVER_STATUS.AVAILABLE : DRIVER_STATUS.OFFLINE);

    const statusConfig = getDriverStatusConfig(currentStatusKey);

    // 4. Resolución de Tiempo
    const mqttTime = realTimeData?.updatedAt;
    const dbTime = data?.driver?.last_time_connected
      ? new Date(data.driver.last_time_connected).getTime()
      : null;
    const lastActivity = mqttTime || dbTime;

    let timeLabel = "";
    if (lastActivity) {
      const diffMinutes = Math.floor(
        (Date.now() - lastActivity) / MS_PER_MINUTE,
      );
      if (diffMinutes > 1) {
        if (diffMinutes >= 60) {
          const hours = Math.floor(diffMinutes / 60);
          timeLabel = `hace ${hours} ${hours === 1 ? "hora" : "horas"}`;
        } else {
          timeLabel = `hace ${diffMinutes} min`;
        }
      }
    }

    return {
      primary: isOnline ? "Conectado" : "Desconectado",
      secondary: statusConfig.label,
      dotClass: isOnline ? "bg-green-500" : "bg-red-500",
      isAnimate: !!statusConfig.isPulse,
      time: timeLabel,
      // Solo mostramos secundario si está online y no es simplemente "disponible" (para evitar redundancia)
      showSecondary: isOnline && currentStatusKey !== DRIVER_STATUS.AVAILABLE,
    };
  }, [data?.driver, realTimeData, loadingMqtt]);
  useEffect(() => {
    if (!id) return;

    fetchDriver();

    const options: IClientOptions = {
      clientId: `web_driver_${id}_${Math.random().toString(16).slice(3)}`,
      clean: true,
      protocol: "wss",
      path: "/mqtt",
      rejectUnauthorized: true,
      keepalive: 60,
      reconnectPeriod: 5000,
      connectTimeout: 30 * 1000,
      username: import.meta.env.VITE_MQTT_USERNAME,
      password: import.meta.env.VITE_MQTT_PASSWORD,
    };
    const client: MqttClient = mqtt.connect(
      import.meta.env.VITE_MQTT_BROKER_URL,
      options,
    );
    const topic = `drivers/${id}/location`;

    // Set loading when starting to search
    setLoadingMqtt(true);

    client.on("connect", () => {
      client.subscribe(topic, (err) => {
        if (err) {
          console.error("Error subscribing to MQTT topic:", err);
          setLoadingMqtt(false);
        }
      });

      // Set a timeout to stop loading if no message is received
      setTimeout(() => {
        setLoadingMqtt(false);
      }, 5000); // 5 seconds timeout
    });

    client.on("message", (_, message: Buffer) => {
      try {
        const data = JSON.parse(message.toString());
        setRealTimeData({
          lat: data.latitude,
          lng: data.longitude,
          battery: data.driver_battery,
          speed: data.speed,
          heading: data.heading,
          orderId: data.orderId,
          tracking_id: data.tracking_id,
          driver_status: data.driver_status ? data.driver_status : data.status,
          updatedAt: Date.now(),
        });
        setLoadingMqtt(false);
      } catch (error) {
        console.error("Error parsing MQTT message:", error);
        setLoadingMqtt(false);
      }
    });

    client.on("error", (err: Error) => {
      console.error("MQTT connection error:", err);
      setLoadingMqtt(false);
    });

    // Cleanup on unmount or driver change
    return () => {
      if (client.connected) {
        client.end();
      }
    };
  }, [id]);

  if (loadingPage) {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="text-gray-500">Cargando conductor...</p>
        </div>
      </div>
    );
  }

  // Check if driver data exists
  if (!data || !data.driver) {
    return (
      <div className="bg-gray-100 min-h-screen flex items-center justify-center">
        <div className="p-6 flex items-center justify-center h-96">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
            <div className="flex flex-col items-center text-center gap-6">
              <div className="p-4 bg-red-50 rounded-full">
                <AlertCircle className="h-12 w-12 text-red-500" />
              </div>

              <div className="space-y-2">
                <h1 className="text-2xl font-semibold text-gray-900">
                  Conductor no encontrado
                </h1>
                <p className="text-gray-600">
                  El conductor que buscas no existe o no tienes permisos para
                  verlo.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full">
                <Link
                  to="/drivers"
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Home className="h-4 w-4" />
                  Ver conductores
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100">
      {/* Header */}
      <header className="bg-white p-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="p-2 hover:bg-gray-50 rounded-xl cursor-pointer"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex flex-col">
          <h1 className="text-2xl font-medium text-gray-800">
            {data.driver.name || "Sin nombre"}
          </h1>
          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1.5">
              <span
                className={`w-2 h-2 rounded-full ${statusInfo.dotClass} ${
                  statusInfo.isAnimate ? "animate-pulse" : ""
                }`}
              />
              <span className="font-medium text-gray-700">
                {statusInfo.primary}
              </span>
            </div>
            {statusInfo.showSecondary && (
              <span className="text-gray-300">•</span>
            )}
            <div className="flex items-center gap-1 text-gray-500">
              {statusInfo.showSecondary && <span>{statusInfo.secondary}</span>}
              {statusInfo.time && (
                <span className="text-xs text-gray-400">
                  ({statusInfo.time})
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <Tabs defaultValue="resumen">
        <TabsList className="bg-white w-full justify-start pl-6 gap-4">
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="info-personal">Info personal</TabsTrigger>
        </TabsList>
        {/* Inactive Driver Warning Banner */}
        {!data.driver.active && (
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mx-6 my-4 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-orange-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-orange-700 break-words sm:text-sm">
                  <span className="block xs:inline">
                    Este conductor se encuentra actualmente inactivo en el
                    sistema. Los datos mostrados son solo de referencia.
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
        <TabsContent value="resumen" className="px-6 pb-6 max-w-7xl mx-auto">
          <DriverResumen
            driver={data.driver}
            metrics={data.metrics}
            realTimeData={realTimeData}
          />
        </TabsContent>
        <TabsContent value="info-personal" className="px-6 max-w-7xl mx-auto">
          <DriverInfoPersonalRefactored
            driver={data.driver}
            setDriver={(updatedDriver: any) =>
              setData((prev: any) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  driver: {
                    ...prev.driver,
                    ...updatedDriver,
                  },
                };
              })
            }
            realTimeData={realTimeData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
